// Chart of Accounts Tree View Component

import React, { useCallback, useRef, useEffect } from 'react';
import {
  ChevronRight,
  Folder,
  FolderOpen,
  FileText,
  MoreHorizontal,
  Plus,
  Search,
  ChevronsUpDown,
  Pencil,
  Trash2,
  FolderPlus,
  FilePlus,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { Loader2 } from 'lucide-react';
import { useChartOfAccountsTree } from './useChartOfAccountsTree';
import { TreeNode } from './treeTypes';

interface ChartOfAccountsTreeProps {
  onCreateAccount?: (prefilledGroupId?: number) => void;
  onEditAccount?: (accountId: number) => void;
  onDeleteAccount?: (accountId: number) => void;
  onCreateAccountGroup?: (prefilledTypeId?: number, prefilledParentId?: number) => void;
  onEditAccountGroup?: (groupId: number) => void;
  onDeleteAccountGroup?: (groupId: number) => void;
}

const ChartOfAccountsTree: React.FC<ChartOfAccountsTreeProps> = ({
  onCreateAccount,
  onEditAccount,
  onDeleteAccount,
  onCreateAccountGroup,
  onEditAccountGroup,
  onDeleteAccountGroup,
}) => {
  const {
    visibleNodes,
    isLoading,
    error,
    expandedIds,
    toggleExpand,
    expandAll,
    collapseAll,
    searchQuery,
    setSearchQuery,
    showInactive,
    setShowInactive,
    selectedNodeId,
    setSelectedNodeId,
    focusedIndex,
    setFocusedIndex,
  } = useChartOfAccountsTree();

  const treeRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const currentNode = visibleNodes[focusedIndex];

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (focusedIndex < visibleNodes.length - 1) {
            setFocusedIndex(focusedIndex + 1);
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          if (focusedIndex > 0) {
            setFocusedIndex(focusedIndex - 1);
          }
          break;

        case 'ArrowRight':
          e.preventDefault();
          if (currentNode?.hasChildren && !expandedIds.has(currentNode.id)) {
            toggleExpand(currentNode.id);
          } else if (currentNode?.hasChildren && expandedIds.has(currentNode.id)) {
            // Move to first child
            const childIndex = visibleNodes.findIndex(
              (n) => n.parentNodeId === currentNode.id
            );
            if (childIndex !== -1) {
              setFocusedIndex(childIndex);
            }
          }
          break;

        case 'ArrowLeft':
          e.preventDefault();
          if (currentNode?.hasChildren && expandedIds.has(currentNode.id)) {
            toggleExpand(currentNode.id);
          } else if (currentNode?.parentNodeId) {
            // Move to parent
            const parentIndex = visibleNodes.findIndex(
              (n) => n.id === currentNode.parentNodeId
            );
            if (parentIndex !== -1) {
              setFocusedIndex(parentIndex);
            }
          }
          break;

        case ' ':
        case 'Space':
          e.preventDefault();
          if (currentNode?.hasChildren) {
            toggleExpand(currentNode.id);
          }
          break;

        case 'Enter':
          e.preventDefault();
          if (currentNode) {
            setSelectedNodeId(currentNode.id);
            if (currentNode.nodeType === 'account') {
              onEditAccount?.(currentNode.rawId);
            } else if (currentNode.nodeType === 'account_group') {
              onEditAccountGroup?.(currentNode.rawId);
            }
          }
          break;

        case 'Home':
          e.preventDefault();
          setFocusedIndex(0);
          break;

        case 'End':
          e.preventDefault();
          setFocusedIndex(visibleNodes.length - 1);
          break;

        case 'f':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            searchInputRef.current?.focus();
          }
          break;
      }
    },
    [
      focusedIndex,
      visibleNodes,
      expandedIds,
      toggleExpand,
      setFocusedIndex,
      setSelectedNodeId,
      onEditAccount,
      onEditAccountGroup,
    ]
  );

  // Scroll focused item into view
  useEffect(() => {
    if (focusedIndex >= 0) {
      const element = treeRef.current?.querySelector(
        `[data-index="${focusedIndex}"]`
      );
      element?.scrollIntoView({ block: 'nearest' });
    }
  }, [focusedIndex]);

  // Render tree node icon
  const renderNodeIcon = (node: TreeNode, isExpanded: boolean) => {
    if (node.nodeType === 'account') {
      return <FileText className="h-4 w-4 text-muted-foreground" />;
    }

    if (isExpanded) {
      return <FolderOpen className="h-4 w-4 text-amber-500" />;
    }

    return <Folder className="h-4 w-4 text-amber-500" />;
  };

  // Render context menu items based on node type
  const renderContextMenuItems = (node: TreeNode) => {
    if (node.nodeType === 'account_type') {
      return (
        <ContextMenuItem
          onClick={() => onCreateAccountGroup?.(node.rawId)}
          className="gap-2"
        >
          <FolderPlus className="h-4 w-4" />
          Add Account Group
        </ContextMenuItem>
      );
    }

    if (node.nodeType === 'account_group') {
      return (
        <>
          <ContextMenuItem
            onClick={() => onCreateAccountGroup?.(node.accountTypeId, node.rawId)}
            className="gap-2"
          >
            <FolderPlus className="h-4 w-4" />
            Add Child Group
          </ContextMenuItem>
          <ContextMenuItem
            onClick={() => onCreateAccount?.(node.rawId)}
            className="gap-2"
          >
            <FilePlus className="h-4 w-4" />
            Add Account
          </ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuItem
            onClick={() => onEditAccountGroup?.(node.rawId)}
            className="gap-2"
          >
            <Pencil className="h-4 w-4" />
            Edit Group
          </ContextMenuItem>
          <ContextMenuItem
            onClick={() => onDeleteAccountGroup?.(node.rawId)}
            className="gap-2 text-destructive focus:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
            Delete Group
          </ContextMenuItem>
        </>
      );
    }

    if (node.nodeType === 'account') {
      return (
        <>
          <ContextMenuItem
            onClick={() => onEditAccount?.(node.rawId)}
            className="gap-2"
          >
            <Pencil className="h-4 w-4" />
            Edit Account
          </ContextMenuItem>
          <ContextMenuItem
            onClick={() => onDeleteAccount?.(node.rawId)}
            className="gap-2 text-destructive focus:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
            Delete Account
          </ContextMenuItem>
        </>
      );
    }

    return null;
  };

  // Render dropdown menu items (same as context menu)
  const renderDropdownMenuItems = (node: TreeNode) => {
    if (node.nodeType === 'account_type') {
      return (
        <DropdownMenuItem
          onClick={() => onCreateAccountGroup?.(node.rawId)}
          className="gap-2"
        >
          <FolderPlus className="h-4 w-4" />
          Add Account Group
        </DropdownMenuItem>
      );
    }

    if (node.nodeType === 'account_group') {
      return (
        <>
          <DropdownMenuItem
            onClick={() => onCreateAccountGroup?.(node.accountTypeId, node.rawId)}
            className="gap-2"
          >
            <FolderPlus className="h-4 w-4" />
            Add Child Group
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => onCreateAccount?.(node.rawId)}
            className="gap-2"
          >
            <FilePlus className="h-4 w-4" />
            Add Account
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => onEditAccountGroup?.(node.rawId)}
            className="gap-2"
          >
            <Pencil className="h-4 w-4" />
            Edit Group
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => onDeleteAccountGroup?.(node.rawId)}
            className="gap-2 text-destructive focus:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
            Delete Group
          </DropdownMenuItem>
        </>
      );
    }

    if (node.nodeType === 'account') {
      return (
        <>
          <DropdownMenuItem
            onClick={() => onEditAccount?.(node.rawId)}
            className="gap-2"
          >
            <Pencil className="h-4 w-4" />
            Edit Account
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => onDeleteAccount?.(node.rawId)}
            className="gap-2 text-destructive focus:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
            Delete Account
          </DropdownMenuItem>
        </>
      );
    }

    return null;
  };

  // Render a single tree row
  const renderTreeRow = (node: TreeNode, index: number) => {
    const isExpanded = expandedIds.has(node.id);
    const isFocused = focusedIndex === index;
    const isSelected = selectedNodeId === node.id;

    return (
      <ContextMenu key={node.id}>
        <ContextMenuTrigger asChild>
          <div
            role="treeitem"
            aria-expanded={node.hasChildren ? isExpanded : undefined}
            aria-selected={isSelected}
            aria-level={node.level + 1}
            data-index={index}
            tabIndex={isFocused ? 0 : -1}
            className={cn(
              'flex items-center h-9 px-2 cursor-pointer group select-none',
              'hover:bg-muted/50 transition-colors',
              isFocused && 'ring-2 ring-ring ring-inset',
              isSelected && 'bg-accent',
              node.isActive === false && 'opacity-50'
            )}
            style={{ paddingLeft: `${node.level * 20 + 8}px` }}
            onClick={() => {
              setSelectedNodeId(node.id);
              setFocusedIndex(index);
            }}
            onDoubleClick={() => {
              if (node.nodeType === 'account') {
                onEditAccount?.(node.rawId);
              } else if (node.nodeType === 'account_group') {
                onEditAccountGroup?.(node.rawId);
              } else if (node.hasChildren) {
                toggleExpand(node.id);
              }
            }}
          >
            {/* Expand/collapse button */}
            {node.hasChildren ? (
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 mr-1 shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpand(node.id);
                }}
              >
                <ChevronRight
                  className={cn(
                    'h-4 w-4 transition-transform duration-200',
                    isExpanded && 'rotate-90'
                  )}
                />
              </Button>
            ) : (
              <span className="w-6 shrink-0" />
            )}

            {/* Icon */}
            <span className="mr-2 shrink-0">
              {renderNodeIcon(node, isExpanded)}
            </span>

            {/* Name and code */}
            <span className="flex-1 truncate">
              <span className={cn(node.isActive === false && 'line-through')}>
                {node.name}
              </span>
              {node.code && (
                <span className="ml-2 text-xs text-muted-foreground font-mono">
                  ({node.code})
                </span>
              )}
            </span>

            {/* Nature badge for account types */}
            {node.nodeType === 'account_type' && node.nature && (
              <Badge
                variant="outline"
                className={cn(
                  'ml-2 shrink-0 text-xs',
                  node.nature === 'debit'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-green-500 text-green-600'
                )}
              >
                {node.nature === 'debit' ? 'Dr' : 'Cr'}
              </Badge>
            )}

            {/* Action menu (visible on hover) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {renderDropdownMenuItems(node)}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </ContextMenuTrigger>

        <ContextMenuContent>
          {renderContextMenuItems(node)}
        </ContextMenuContent>
      </ContextMenu>
    );
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 text-destructive">
        Error loading accounts: {error.message}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-4 p-4 border-b">
        {/* Search */}
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            type="search"
            placeholder="Search accounts..."
            className="pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Show inactive toggle */}
        <div className="flex items-center gap-2">
          <Checkbox
            id="show-inactive"
            checked={showInactive}
            onCheckedChange={(checked) => setShowInactive(checked === true)}
          />
          <Label htmlFor="show-inactive" className="text-sm cursor-pointer">
            Show inactive
          </Label>
        </div>

        {/* Expand/Collapse all */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            if (expandedIds.size > 0) {
              collapseAll();
            } else {
              expandAll();
            }
          }}
          className="gap-2"
        >
          <ChevronsUpDown className="h-4 w-4" />
          {expandedIds.size > 0 ? 'Collapse All' : 'Expand All'}
        </Button>

        {/* New button */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" className="gap-2">
              <Plus className="h-4 w-4" />
              New
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => onCreateAccountGroup?.()}
              className="gap-2"
            >
              <FolderPlus className="h-4 w-4" />
              Account Group
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onCreateAccount?.()}
              className="gap-2"
            >
              <FilePlus className="h-4 w-4" />
              Account
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Tree content */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : visibleNodes.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <FileText className="h-12 w-12 mb-4" />
          <p>No accounts found</p>
          {searchQuery && (
            <p className="text-sm mt-2">
              Try adjusting your search or{' '}
              <button
                className="text-primary underline"
                onClick={() => setSearchQuery('')}
              >
                clear the filter
              </button>
            </p>
          )}
        </div>
      ) : (
        <ScrollArea className="flex-1">
          <div
            ref={treeRef}
            role="tree"
            aria-label="Chart of Accounts"
            tabIndex={0}
            onKeyDown={handleKeyDown}
            className="py-2 focus:outline-none"
          >
            {visibleNodes.map((node, index) => renderTreeRow(node, index))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
};

export default ChartOfAccountsTree;
